import React from 'react';
import { Users, DollarSign, TrendingDown, Minus, Calendar } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import { AdminBalanceSummary as AdminBalanceSummaryType } from '../../services/billingService';

interface AdminBalanceSummaryProps {
  summary: AdminBalanceSummaryType;
  loading?: boolean;
}

const AdminBalanceSummary: React.FC<AdminBalanceSummaryProps> = ({ summary, loading = false }) => {
  const { theme } = useAppSelector((state) => state.ui);

  const formatBalance = (balance: string): string => {
    const num = parseFloat(balance);
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const statCards = [
    {
      label: 'Total Hosts',
      value: summary.total_hosts.toLocaleString(),
      icon: Users,
      color: theme === 'dark' ? 'text-blue-400' : 'text-blue-600',
      bgColor: theme === 'dark' ? 'bg-blue-500/10' : 'bg-blue-50',
    },
    {
      label: 'Total Balance',
      value: `${formatBalance(summary.total_balance)} JOD`,
      icon: DollarSign,
      color: theme === 'dark' ? 'text-green-400' : 'text-green-600',
      bgColor: theme === 'dark' ? 'bg-green-500/10' : 'bg-green-50',
    },
    {
      label: 'Negative Balance',
      value: summary.negative_balance_count.toLocaleString(),
      icon: TrendingDown,
      color: theme === 'dark' ? 'text-red-400' : 'text-red-600',
      bgColor: theme === 'dark' ? 'bg-red-500/10' : 'bg-red-50',
    },
    {
      label: 'Zero Balance',
      value: summary.zero_balance_count.toLocaleString(),
      icon: Minus,
      color: theme === 'dark' ? 'text-gray-400' : 'text-gray-600',
      bgColor: theme === 'dark' ? 'bg-gray-500/10' : 'bg-gray-50',
    },
    {
      label: 'Total Events',
      value: summary.total_events.toLocaleString(),
      icon: Calendar,
      color: theme === 'dark' ? 'text-purple-400' : 'text-purple-600',
      bgColor: theme === 'dark' ? 'bg-purple-500/10' : 'bg-purple-50',
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={`rounded-lg border p-4 animate-pulse ${
              theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <div className={`h-4 w-20 rounded mb-2 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
            <div className={`h-8 w-24 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {statCards.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <div
            key={index}
            className={`rounded-lg border p-4 transition-shadow hover:shadow-md ${
              theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {stat.label}
              </p>
              <div className={`p-2 rounded ${stat.bgColor}`}>
                <Icon className={`h-4 w-4 ${stat.color}`} />
              </div>
            </div>
            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {stat.value}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default AdminBalanceSummary;

